import {
  ControlContainer,
  FullscreenButton,
  PlayButton,
  PlayButtonCenter,
  ThemeProvider,
  TimeDisplay,
  Timeline,
  Video as VideoComponent,
  VolumeSlider,
} from '@volkswagen-onehub/components-core';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ScrollHintArrow from '../ScrollHintArrow';

gsap.registerPlugin(ScrollTrigger);

const BACKGROUND_IMAGE =
  'https://vgedonehubappsstaweupro.blob.core.windows.net/storage/images-gti50/GTI_Gallo/video_background/background_video.jpg';
const DESKTOP_VIDEO_URL = 'https://assets.volkswagen.com/is/content/volkswagenag/MASTER_VOLKSWAGEN_ANIVERSARIO_30sec_1-0x720-3000k';
const MOBILE_VIDEO_URL = 'https://assets.volkswagen.com/is/content/volkswagenag/MASTER_VOLKSWAGEN_ANIVERSARIO_9x16_1_OLV_30sec_wip_20260409_v02_1-0x720-3000k';
const VIDEO_BREAKPOINT = 560;
const GTI_RED = '#C90010';

const Section = styled.section`
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-image:
    linear-gradient(180deg, rgba(0, 0, 0, 0.28) 0%, rgba(0, 0, 0, 0.72) 100%), url('${BACKGROUND_IMAGE}');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 10vh 8vw 18vh;

  @media screen and (max-width: 768px) {
    align-items: center;
    justify-content: flex-start;
    padding: 18vh 18vw 22vh;
  }
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(18px, 2vw, 32px);

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
`;

const GtiMark = styled.svg`
  width: auto;
  height: min(174px, 9.0625vw);
  flex: 0 0 auto;

  @media screen and (max-width: 768px) {
    width: clamp(180px, 48vw, 260px);
    height: auto;
  }
`;

const Divider = styled.span`
  width: 2px;
  height: clamp(72px, 10vw, 132px);
  background: rgba(226, 0, 18, 0.85);

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const FiftyWrap = styled.div`
  display: flex;
  align-items: flex-end;
  gap: clamp(10px, 1vw, 18px);

  @media screen and (max-width: 768px) {
    gap: 10px;
  }
`;

const FiveMark = styled.svg`
  width: auto;
  height: min(174px, 9.0625vw);
  flex: 0 0 auto;

  @media screen and (max-width: 768px) {
    width: clamp(120px, 32vw, 220px);
    height: auto;
  }
`;

const ZeroMark = styled.svg`
  width: auto;
  height: min(174px, 9.0625vw);
  flex: 0 0 auto;

  @media screen and (max-width: 768px) {
    width: clamp(136px, 36vw, 240px);
    height: auto;
  }
`;

const FinalVideo = styled.div`
  position: absolute;
  left: 40%;
  bottom: 0;
  z-index: 2;
  width: 20vw;
  height: auto;
  aspect-ratio: 16 / 9;
  border-radius: 18px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.45);
  opacity: 1;
  overflow: hidden;

  & > div {
    width: 100%;
    height: 100%;
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 18px;
  }

  button {
    color: ${GTI_RED};
  }

  [role='slider'] {
    background: rgba(255, 255, 255, 0.9);
  }

  [role='slider']::before,
  [role='slider']::after {
    background: ${GTI_RED};
  }

  [role='slider'] > div {
    background: ${GTI_RED};
  }

  @media screen and (max-width: 768px) {
    width: 44vw;
    min-width: 180px;
    left: 28%;
  }
`;

const ScrollHintArrowLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  pointer-events: none;
`;

const StyledPlayButtonCenter = styled(PlayButtonCenter)`
  background-color: rgba(0, 0, 0, 0.8);
  &:hover, &:focus, &:active, &:focus-visible{
    background-color: rgba(0, 0, 0, 0.8) !important;
  }
`;

const StyledControlContainer = styled(ControlContainer)`
  background-color: rgba(0, 0, 0, 0.8);
`;

const StyledVolumeSliderWrapper = styled.span`
  & > div > div {
    background-color: rgba(0, 0, 0, 0.8);
  } 
`;

export default function GalloReveal() {
  const containerRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const scrollTriggerRef = useRef<ScrollTrigger | null>(null);
  const [isMin560, setIsMin560] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= VIDEO_BREAKPOINT : true,
  );

  const videoSrc = isMin560 ? DESKTOP_VIDEO_URL : MOBILE_VIDEO_URL;

  const syncScrollAnimation = () => {
    const scrollTrigger = scrollTriggerRef.current;
    if (!scrollTrigger) {
      return;
    }

    scrollTrigger.refresh();
    scrollTrigger.update();
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setIsMin560(window.innerWidth >= VIDEO_BREAKPOINT);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const videoElement = videoRef.current?.querySelector('video');

    if (!videoElement) {
      return;
    }

    videoElement.pause();
    videoElement.load();

    videoElement.addEventListener('loadeddata', syncScrollAnimation);
    videoElement.addEventListener('loadedmetadata', syncScrollAnimation);

    return () => {
      videoElement.removeEventListener('loadeddata', syncScrollAnimation);
      videoElement.removeEventListener('loadedmetadata', syncScrollAnimation);
    };
  }, [videoSrc]);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const timeline = gsap.timeline({
        scrollTrigger: {
          id: 'gallo-reveal-pin',
          trigger: containerRef.current,
          start: 'top top',
          end: '+=300%',
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      timeline.fromTo(
        videoRef.current,
        {
          y: '100%',
          scale: 1,
          opacity: 1,
        },
        {
          y: '0%',
          duration: 1,
          ease: 'none',
        },
        0.15,
      );

      timeline.to(videoRef.current, {
        left: '0%',
        bottom: 0,
        width: '100vw',
        height: '100vh',
        borderRadius: 0,
        duration: 1.15,
        ease: 'power2.out',
      });

      scrollTriggerRef.current = timeline.scrollTrigger ?? null;
    }, containerRef);

    return () => {
      scrollTriggerRef.current = null;
      ctx.revert();
    };
  }, [isMin560]);

  return (
    <Section ref={containerRef} id="gallo-reveal-module">
      <Content>
        <Lockup>
          <GtiMark xmlns="http://www.w3.org/2000/svg" width="241" height="60" viewBox="0 0 241 60" fill="none">
            <path
              d="M132.083 13.189V46.9268H119.468V58.3001H159.513V46.9268H146.945V13.189H165.917V19.3535H177.72V1.52893H101.261V19.3535H113.064V13.189H132.083Z"
              fill="#C90010"
            />
            <path
              d="M213.465 13.4757H200.85V1.52893H240.895V13.4757H228.327V46.9268H240.895V58.3001H200.85V46.9268H213.465V13.4757Z"
              fill="#C90010"
            />
            <path
              d="M67.0932 14.0972C59.1127 12.8548 50.7978 12.2813 42.7695 12.2813C39.6156 12.2813 36.5094 12.3769 33.4032 12.6636C30.3926 12.9503 27.0953 13.476 24.2759 14.6707C21.2653 15.9131 18.9237 17.7768 17.4423 20.7874C16.0565 23.6069 15.722 26.9042 15.722 29.9626C15.722 33.0209 16.1043 36.3183 17.4423 39.1855C18.8759 42.1961 21.2175 44.1554 24.1803 45.4934C26.9998 46.7359 30.3449 47.3093 33.3555 47.596C36.5094 47.9305 39.6634 47.9783 42.8173 47.9783C46.4969 47.9783 50.2243 47.8828 53.9039 47.6438C57.2013 47.4527 61.6932 47.0704 64.9428 46.4969C65.4684 46.4013 66.1375 46.258 66.5198 45.8279C66.9021 45.35 66.9498 44.681 66.9498 44.1554C67.0454 42.1005 67.0454 40.0457 67.0932 37.9908H49.2686V26.3785H81.0471C81.2382 33.0687 81.286 40.6191 80.9515 47.2615C80.8559 49.6031 80.6648 52.327 78.9922 54.334C77.2719 56.3411 74.6914 56.9623 72.3498 57.4402C62.8879 59.3039 52.3747 59.9251 42.8173 59.9251C37.8952 59.9251 32.7342 59.7818 27.8599 59.1127C22.9856 58.4437 17.9202 57.2968 13.5238 55.003C3.3451 49.6509 0 40.4757 0 29.9148C0 19.3538 3.39289 10.322 13.5238 5.11323C17.9202 2.86723 22.9856 1.62476 27.8121 0.907956C32.6864 0.238936 37.7997 0 42.7217 0C48.8863 0 55.2898 0.382297 61.4065 1.05132C67.141 1.67255 74.2613 2.77166 79.8524 4.30085V19.0671H67.0454V14.0972"
              fill="#C90010"
            />
          </GtiMark>
          <Divider />
          <FiftyWrap>
            <FiveMark xmlns="http://www.w3.org/2000/svg" width="251" height="204" viewBox="0 0 251 204" fill="none">
              <path
                d="M122.001 203.382C76.8418 203.382 40.5713 197.839 3.05838 190.145L0 148.666C32.5909 155.404 78.0365 162.19 122.001 162.19C188.998 162.19 201.28 155.117 201.28 135.477C201.28 116.744 188.664 113.064 121.666 113.064H1.52919V0H236.021L239.701 40.5713H49.4597V72.5409H121.379C225.842 72.5409 250.453 89.4576 250.453 136.146C250.453 178.867 220.633 203.43 122.001 203.43V203.382Z"
                fill="white"
              />
            </FiveMark>
            <ZeroMark xmlns="http://www.w3.org/2000/svg" width="280" height="208" viewBox="0 0 280 208" fill="none">
              <path
                d="M0 104.463C0 29.1502 36.2705 0 140.112 0C244.288 0 279.937 28.8635 279.937 104.176C279.937 179.489 243.38 207.731 139.491 207.731C35.6014 207.731 0 179.775 0 104.463ZM228.327 104.463C228.327 53.7606 210.503 41.1448 139.539 41.1448C68.8613 41.1448 51.6579 53.1393 51.6579 104.128C51.6579 153.923 66.0897 166.491 140.16 166.491C214.517 166.491 228.375 154.83 228.375 104.415L228.327 104.463Z"
                fill="white"
              />
            </ZeroMark>
          </FiftyWrap>
        </Lockup>
      </Content>

      <FinalVideo ref={videoRef}>
        <ThemeProvider
          theme={{
            colors: {
              button: {
                primary: {
                  default: GTI_RED,
                  active: GTI_RED,
                },
                tertiary: {
                  default: GTI_RED,
                  active: GTI_RED,
                },
              },
            },
          }}
        >
          <VideoComponent src={videoSrc} playsInline autoPlay>
            <StyledPlayButtonCenter playTitle="Play video" />
            <StyledControlContainer hideInitially>
              <PlayButton playTitle="Play video" pauseTitle="Pause video" />
              <Timeline />
              <TimeDisplay />
              <StyledVolumeSliderWrapper id='xdd'>
                <VolumeSlider muteTitle="Mute video" unmuteTitle="Unmute video" />
              </StyledVolumeSliderWrapper>
              <FullscreenButton openTitle="Open video in fullscreen" closeTitle="Close fullscreen" />
            </StyledControlContainer>
          </VideoComponent>
        </ThemeProvider>
      </FinalVideo>

      <ScrollHintArrowLayer>
        <ScrollHintArrow pinId="gallo-reveal-pin" trigger={containerRef} targetSelector="#detail-gti50-module" />
      </ScrollHintArrowLayer>
    </Section>
  );
}
